import { iNotification, Store } from "react-notifications-component";
import create from "zustand";
import Constants from "./constants";
import {
  FeatureFlagKey,
  loadAllLocalOverrides,
  setLocalOverride,
} from "./featureFlags";
import {
  getBeneficiosAplicarLocalOverride,
  setBeneficiosAplicarLocalOverride,
} from "./beneficiosAplicar";

interface OFVNotification {
  visible?: boolean;
  title?: string;
  message?: string;
  isError: boolean;
  duration?: number;
  persistent?: boolean;
}

/** Session stored under Constants.USER: JWT plus optional cedula for complementario APIs */
export interface OFVUserSession {
  token: string;
  cedula?: string;
}

interface GlobalState {
  user: OFVUserSession | null;
  notification: OFVNotification;
  bpmsActive?: boolean;
  loading?: boolean;
  /** Flag values resolved from the backend Parametros (real prod switch). */
  featureFlagsServer: Partial<Record<FeatureFlagKey, boolean>>;
  /** Per-browser overrides set from /devtools (highest priority). */
  featureFlagOverrides: Partial<Record<FeatureFlagKey, boolean>>;
  /** CSV de COD_BENEFICIO con botón "Aplicar", resuelto desde el backend. */
  beneficiosAplicarServer?: string;
  /** Override por navegador (gestionado en /devtools) del CSV anterior. */
  beneficiosAplicarOverride?: string;
  updateUser: (user: OFVUserSession | null) => void;
  showNotification: (notification: OFVNotification) => void;
  updateBPMSActive: (bpmsActive: boolean) => void; 
  hideNotification: (isError: boolean) => void;
  showLoading: () => void;
  hideLoading: () => void;
  setServerFeatureFlags: (
    flags: Partial<Record<FeatureFlagKey, boolean>>
  ) => void;
  setFeatureFlagOverride: (
    key: FeatureFlagKey,
    value: boolean | undefined
  ) => void;
  setBeneficiosAplicarServer: (value: string | undefined) => void;
  setBeneficiosAplicarOverride: (value: string | undefined) => void;
}

export const useGlobalStore = create<GlobalState>((set) => ({
  // initial state
  user: JSON.parse(localStorage.getItem("user") || "null") as OFVUserSession | null,
  bpmsActive: localStorage.getItem(Constants.ACTIVAR_BPMS_BENEFICIO) === "true", 
  featureFlagsServer: {},
  featureFlagOverrides: loadAllLocalOverrides(),
  beneficiosAplicarServer: undefined,
  beneficiosAplicarOverride: getBeneficiosAplicarLocalOverride(),
  notification: {
    visible: false,
    isError: false,
  },

  // methods for manipulating state
  updateUser: (user: any) => {
    localStorage.setItem(Constants.USER, JSON.stringify(user));
    set((state) => ({
      ...state,
      user: user,
    }));
  },
  updateBPMSActive: (bpmsActive: boolean) => {
    set((state) => ({
      ...state,
      bpmsActive, // Update bpmsActive state
    }));
    localStorage.setItem(Constants.ACTIVAR_BPMS_BENEFICIO, JSON.stringify(bpmsActive)); // Persist to localStorage
  },
  setServerFeatureFlags: (flags) => {
    set((state) => ({
      ...state,
      featureFlagsServer: { ...state.featureFlagsServer, ...flags },
    }));
  },
  setFeatureFlagOverride: (key, value) => {
    setLocalOverride(key, value);
    set((state) => {
      const next = { ...state.featureFlagOverrides };
      if (value === undefined) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return { ...state, featureFlagOverrides: next };
    });
  },
  setBeneficiosAplicarServer: (value) => {
    set((state) => ({ ...state, beneficiosAplicarServer: value }));
  },
  setBeneficiosAplicarOverride: (value) => {
    setBeneficiosAplicarLocalOverride(value);
    set((state) => ({ ...state, beneficiosAplicarOverride: value }));
  },
  showNotification: (notification: OFVNotification) => {
    const notificationA: iNotification = {
      title: notification.title,
      message: notification.message,
      type: notification.isError ? "danger" : "success",
      insert: "top",
      container: "top-right",
      animationIn: ["animate__animated animate__fadeIn"], // `animate.css v4` classes
      animationOut: ["animate__animated animate__fadeOut"], // `animate.css v4` classes
    };
    Store.addNotification({
      ...notificationA,
      dismiss: {
        duration: notification.persistent
          ? 0
          : (notification.duration ?? 10000),
        onScreen: true,
        showIcon: true,
        pauseOnHover: !notification.persistent,
      },
    });
  },
  hideNotification: (isError: boolean) => {
    set((state) => ({
      ...state,
      notification: { visible: false, isError: isError },
    }));
  },
  showLoading: () => {
    set((state) => ({
      ...state,
      loading: true,
    }));
  },
  hideLoading: () => {
    set((state) => ({
      ...state,
      loading: false,
    }));
  },
}));
